import React, { useCallback, useState, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  ConnectionMode,
  Panel,
  NodeProps,
  Handle,
  Position,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Plus,
  Trash2,
  Download,
  MessageSquare,
  Copy,
  Zap,
  Save,
  ArrowRight,
  Settings,
  DollarSign,
  Package,
  Flag,
  Store,
  Bell,
  Navigation,
  Film,
  Target,
  Gift,
  ChevronDown,
  GitMerge,
  Code,
  CheckCircle,
  XCircle,
  List,
  FolderOpen,
} from 'lucide-react';

// Types
type NodeType = 'dialog' | 'action' | 'event' | 'condition';

type DialogChoice = {
  text: string;
  go?: string;
};

type BaseNode = {
  id: string;
  type: NodeType;
  position?: { x: number; y: number };
};

type DialogNodeData = BaseNode & {
  type: 'dialog';
  npc?: string;
  choices?: DialogChoice[];
};

type ActionNodeData = BaseNode & {
  type: 'action';
  action: string;
  params: Record<string, any>;
};

type EventNodeData = BaseNode & {
  type: 'event';
  event: string;
  params: Record<string, any>;
};

type ConditionNodeData = BaseNode & {
  type: 'condition';
  condition_type: 'has_item' | 'has_flag' | 'level_check' | 'custom';
  params: Record<string, any>;
  on_true?: string;
  on_false?: string;
};

type AnyNodeData = DialogNodeData | ActionNodeData | EventNodeData | ConditionNodeData;

type DialogTree = {
  dialog_tree_id: string;
  root: string;
  nodes: Record<string, AnyNodeData>;
};

type SavedProject = {
  id: string;
  name: string;
  updated: number;
  data: DialogTree;
};

const uid = (prefix = 'node') => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

// Templates
const ACTION_TEMPLATES = [
  { id: 'add_money', label: 'Добавить деньги', icon: DollarSign, color: 'emerald', params: { amount: 100 } },
  { id: 'add_item', label: 'Добавить предмет', icon: Package, color: 'blue', params: { item: 'phone', count: 1 } },
  { id: 'remove_item', label: 'Удалить предмет', icon: Trash2, color: 'red', params: { item: 'phone', count: 1 } },
  { id: 'set_flag', label: 'Установить флаг', icon: Flag, color: 'purple', params: { key: 'quest_done', value: true } },
  { id: 'open_shop', label: 'Открыть магазин', icon: Store, color: 'orange', params: { shop_id: 'phone_shop' } },
  { id: 'notify', label: 'Уведомление', icon: Bell, color: 'yellow', params: { text: 'Задание выполнено!' } },
  { id: 'teleport', label: 'Телепорт', icon: Navigation, color: 'cyan', params: { x: 0, y: 0, z: 0 } },
  { id: 'play_anim', label: 'Анимация', icon: Film, color: 'pink', params: { anim_dict: 'anim@amb@nightclub', anim_name: 'dance' } },
  { id: 'custom_function', label: 'Вызвать функцию', icon: Code, color: 'slate', params: { function_name: 'show_join_screen', args: {} } },
];

const EVENT_TEMPLATES = [
  { id: 'quest:complete', label: 'Завершить квест', icon: Target, color: 'green', params: { quest_id: '' } },
  { id: 'quest:start', label: 'Начать квест', icon: Target, color: 'blue', params: { quest_id: '' } },
  { id: 'give_reward', label: 'Выдать награду', icon: Gift, color: 'amber', params: { reward_id: '' } },
];

const CONDITION_TYPES = [
  { id: 'has_item', label: 'Есть предмет', params: { item: 'military_ticket' } },
  { id: 'has_flag', label: 'Флаг установлен', params: { flag: 'passed_test' } },
  { id: 'level_check', label: 'Проверка уровня', params: { min_level: 5 } },
  { id: 'custom', label: 'Кастомная проверка', params: { function_name: 'CheckPlayerRank' } },
];

// Dialog Node
function CustomDialogNode({ data, selected }: NodeProps) {
  const node = data.node as DialogNodeData;
  const isRoot = data.isRoot as boolean;
  const hasChoices = node.choices && node.choices.length > 0;

  return (
    <div
      className={`rounded-xl border-2 bg-white shadow-xl min-w-[280px] max-w-[320px] transition-all ${
        selected ? 'border-primary ring-4 ring-primary/20' : 'border-slate-300'
      } ${isRoot ? 'border-green-500 ring-2 ring-green-200' : ''}`}
    >
      <Handle type="target" position={Position.Top} className="w-4 h-4 !bg-primary !border-2 !border-white" />

      <div className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <MessageSquare className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {isRoot && (
              <div className="inline-flex items-center gap-1 text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded mb-1">
                ▶ START
              </div>
            )}
            <div className="text-xs text-slate-400 font-mono truncate">{node.id}</div>
          </div>
        </div>

        {node.npc && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200">
            <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{node.npc}</div>
          </div>
        )}

        {hasChoices && (
          <div className="space-y-2 pt-2 border-t">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Варианты:</div>
            {node.choices!.map((choice, idx) => (
              <div key={idx} className="relative group">
                <div className="text-xs bg-gradient-to-r from-purple-50 to-pink-50 border-purple-300 rounded-lg px-3 py-2 border-2 transition-all hover:shadow-md flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-white text-[10px] flex items-center justify-center font-bold">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="block text-slate-800 leading-tight">{choice.text || '(пусто)'}</span>
                  </div>
                  {choice.go && <ArrowRight className="w-3.5 h-3.5 text-purple-400 flex-shrink-0 mt-0.5" />}
                </div>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`choice-${idx}`}
                  className="!w-3 !h-3 !bg-purple-500 !border-2 !border-white !right-[-6px] !top-1/2 !-translate-y-1/2"
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} id="default" className="w-4 h-4 !bg-primary !border-2 !border-white" />
    </div>
  );
}

// Action Node
function ActionNode({ data, selected }: NodeProps) {
  const node = data.node as ActionNodeData;
  const template = ACTION_TEMPLATES.find(t => t.id === node.action);
  const Icon = template?.icon || Settings;
  const color = template?.color || 'slate';

  const colorClasses = {
    emerald: 'border-emerald-400 bg-emerald-50 text-emerald-900',
    blue: 'border-blue-400 bg-blue-50 text-blue-900',
    red: 'border-red-400 bg-red-50 text-red-900',
    purple: 'border-purple-400 bg-purple-50 text-purple-900',
    orange: 'border-orange-400 bg-orange-50 text-orange-900',
    yellow: 'border-yellow-400 bg-yellow-50 text-yellow-900',
    cyan: 'border-cyan-400 bg-cyan-50 text-cyan-900',
    pink: 'border-pink-400 bg-pink-50 text-pink-900',
    slate: 'border-slate-400 bg-slate-50 text-slate-900',
  }[color] || 'border-slate-400 bg-slate-50 text-slate-900';

  return (
    <div className={`rounded-xl border-2 shadow-lg min-w-[220px] max-w-[260px] transition-all ${selected ? 'ring-4 ring-offset-2 ring-primary/30' : ''} ${colorClasses}`}>
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-slate-600 !border-2 !border-white" />
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-white/80 shadow-sm">
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wide opacity-60">Действие</div>
            <div className="text-sm font-semibold truncate">{template?.label || node.action}</div>
          </div>
        </div>
        {Object.keys(node.params).length > 0 && (
          <div className="text-xs bg-white/50 rounded p-2 space-y-1 font-mono">
            {Object.entries(node.params).map(([key, value]) => (
              <div key={key} className="flex gap-1">
                <span className="opacity-60">{key}:</span>
                <span className="font-semibold truncate">{String(value)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-slate-600 !border-2 !border-white" />
    </div>
  );
}

// Event Node
function EventNode({ data, selected }: NodeProps) {
  const node = data.node as EventNodeData;
  const template = EVENT_TEMPLATES.find(t => t.id === node.event);
  const Icon = template?.icon || Zap;
  const color = template?.color || 'amber';

  const colorClasses = {
    green: 'border-green-400 bg-green-50 text-green-900',
    blue: 'border-blue-400 bg-blue-50 text-blue-900',
    amber: 'border-amber-400 bg-amber-50 text-amber-900',
  }[color] || 'border-amber-400 bg-amber-50 text-amber-900';

  return (
    <div className={`rounded-xl border-2 shadow-lg min-w-[220px] max-w-[260px] transition-all ${selected ? 'ring-4 ring-offset-2 ring-primary/30' : ''} ${colorClasses}`}>
      <Handle type="target" position={Position.Left} className="w-4 h-4 !bg-amber-600 !border-2 !border-white" />
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-white/80 shadow-sm">
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wide opacity-60">Событие</div>
            <div className="text-sm font-semibold truncate">{template?.label || node.event}</div>
          </div>
        </div>
        <div className="text-xs bg-white/50 rounded px-2 py-1 font-mono font-semibold">{node.event}</div>
        {Object.keys(node.params).length > 0 && (
          <div className="text-xs bg-white/50 rounded p-2 space-y-1 font-mono">
            {Object.entries(node.params).map(([key, value]) => (
              <div key={key} className="flex gap-1">
                <span className="opacity-60">{key}:</span>
                <span className="font-semibold truncate">{String(value)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="w-4 h-4 !bg-amber-600 !border-2 !border-white" />
    </div>
  );
}

// Condition Node (NEW!)
function ConditionNode({ data, selected }: NodeProps) {
  const node = data.node as ConditionNodeData;
  const condType = CONDITION_TYPES.find(t => t.id === node.condition_type);

  return (
    <div
      className={`rounded-xl border-2 shadow-xl min-w-[240px] max-w-[280px] transition-all bg-gradient-to-br from-orange-50 to-amber-50 ${
        selected ? 'border-orange-500 ring-4 ring-orange-200' : 'border-orange-400'
      }`}
      style={{ clipPath: 'polygon(10% 0%, 90% 0%, 100% 50%, 90% 100%, 10% 100%, 0% 50%)' }}
    >
      <Handle type="target" position={Position.Top} className="w-4 h-4 !bg-orange-500 !border-2 !border-white" />

      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2 justify-center">
          <GitMerge className="w-5 h-5 text-orange-600" />
          <div className="text-center">
            <div className="text-xs font-bold uppercase tracking-wide text-orange-600">Условие</div>
            <div className="text-sm font-semibold text-orange-900">{condType?.label || node.condition_type}</div>
          </div>
        </div>

        {Object.keys(node.params).length > 0 && (
          <div className="text-xs bg-white/70 rounded p-2 space-y-1 font-mono text-center">
            {Object.entries(node.params).map(([key, value]) => (
              <div key={key}>
                <span className="opacity-60">{key}:</span> <span className="font-semibold">{String(value)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between text-xs font-bold pt-2">
          <div className="flex items-center gap-1 text-red-600">
            <XCircle className="w-3 h-3" />
            FALSE
          </div>
          <div className="flex items-center gap-1 text-green-600">
            <CheckCircle className="w-3 h-3" />
            TRUE
          </div>
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Left}
        id="false"
        className="w-4 h-4 !bg-red-500 !border-2 !border-white !top-[50%]"
        style={{ left: '-8px' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        className="w-4 h-4 !bg-green-500 !border-2 !border-white !top-[50%]"
        style={{ right: '-8px' }}
      />
    </div>
  );
}

const nodeTypes = {
  dialogNode: CustomDialogNode,
  actionNode: ActionNode,
  eventNode: EventNode,
  conditionNode: ConditionNode,
};

// LocalStorage helpers
const STORAGE_KEY = 'wisequest_dialogs';

function saveToLocalStorage(projects: SavedProject[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
}

function loadFromLocalStorage(): SavedProject[] {
  const data = localStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : [];
}

export default function ImprovedDialogEditor() {
  const [dialogTree, setDialogTree] = useState<DialogTree>({
    dialog_tree_id: 'new_dialog',
    root: 'node_start',
    nodes: {
      node_start: {
        id: 'node_start',
        type: 'dialog',
        npc: 'NPC: Привет! Нужна помощь?',
        choices: [
          { text: 'Да, помоги!', go: '' },
          { text: 'Нет, спасибо', go: '' },
        ],
      },
    },
  });

  const [selectedNodeId, setSelectedNodeId] = useState<string>('node_start');
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showProjectList, setShowProjectList] = useState(false);
  const [savedProjects, setSavedProjects] = useState<SavedProject[]>([]);
  const [lastSaved, setLastSaved] = useState<number>(Date.now());

  // Load projects on mount
  useEffect(() => {
    setSavedProjects(loadFromLocalStorage());
  }, []);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      handleSave();
    }, 30000);
    return () => clearInterval(interval);
  }, [dialogTree]);

  const handleSave = () => {
    const projects = loadFromLocalStorage();
    const existing = projects.findIndex(p => p.id === dialogTree.dialog_tree_id);

    const project: SavedProject = {
      id: dialogTree.dialog_tree_id,
      name: dialogTree.dialog_tree_id,
      updated: Date.now(),
      data: dialogTree,
    };

    if (existing >= 0) {
      projects[existing] = project;
    } else {
      projects.push(project);
    }

    saveToLocalStorage(projects);
    setSavedProjects(projects);
    setLastSaved(Date.now());
  };

  const handleLoad = (project: SavedProject) => {
    setDialogTree(project.data);
    setSelectedNodeId(project.data.root);
    setShowProjectList(false);
  };

  const handleDelete = (projectId: string) => {
    const projects = savedProjects.filter(p => p.id !== projectId);
    saveToLocalStorage(projects);
    setSavedProjects(projects);
  };

  const getReactFlowNodes = (): Node[] => {
    return Object.entries(dialogTree.nodes).map(([nodeId, node], index) => {
      const col = index % 4;
      const row = Math.floor(index / 4);
      const isRoot = nodeId === dialogTree.root;

      let nodeType = 'dialogNode';
      if (node.type === 'action') nodeType = 'actionNode';
      if (node.type === 'event') nodeType = 'eventNode';
      if (node.type === 'condition') nodeType = 'conditionNode';

      // Use saved position or calculate default grid position
      const position = node.position || { x: col * 380, y: row * 300 };

      return {
        id: nodeId,
        type: nodeType,
        position,
        data: { node, isRoot },
      };
    });
  };

  const getReactFlowEdges = (): Edge[] => {
    const edges: Edge[] = [];

    Object.entries(dialogTree.nodes).forEach(([nodeId, node]) => {
      if (node.type === 'dialog' && node.choices) {
        node.choices.forEach((choice, idx) => {
          if (choice.go && dialogTree.nodes[choice.go]) {
            edges.push({
              id: `${nodeId}-choice${idx}-${choice.go}`,
              source: nodeId,
              sourceHandle: `choice-${idx}`,
              target: choice.go,
              animated: true,
              label: `${idx + 1}`,
              style: { stroke: '#a855f7', strokeWidth: 2 },
              labelStyle: { fill: '#a855f7', fontWeight: 700, fontSize: 14 },
              labelBgStyle: { fill: '#faf5ff' },
              markerEnd: { type: MarkerType.ArrowClosed, color: '#a855f7' },
            });
          }
        });
      }

      if (node.type === 'condition') {
        if (node.on_true && dialogTree.nodes[node.on_true]) {
          edges.push({
            id: `${nodeId}-true-${node.on_true}`,
            source: nodeId,
            sourceHandle: 'true',
            target: node.on_true,
            animated: true,
            label: 'TRUE',
            style: { stroke: '#22c55e', strokeWidth: 3 },
            labelStyle: { fill: '#22c55e', fontWeight: 700 },
            labelBgStyle: { fill: '#f0fdf4' },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#22c55e' },
          });
        }
        if (node.on_false && dialogTree.nodes[node.on_false]) {
          edges.push({
            id: `${nodeId}-false-${node.on_false}`,
            source: nodeId,
            sourceHandle: 'false',
            target: node.on_false,
            animated: true,
            label: 'FALSE',
            style: { stroke: '#ef4444', strokeWidth: 3 },
            labelStyle: { fill: '#ef4444', fontWeight: 700 },
            labelBgStyle: { fill: '#fef2f2' },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444' },
          });
        }
      }
    });

    return edges;
  };

  const [nodes, setNodes, onNodesChange] = useNodesState(getReactFlowNodes());
  const [edges, setEdges, onEdgesChange] = useEdgesState(getReactFlowEdges());

  React.useEffect(() => {
    setNodes((currentNodes) => {
      const newNodes = getReactFlowNodes();

      // Preserve current positions from React Flow
      return newNodes.map(newNode => {
        const existingNode = currentNodes.find(n => n.id === newNode.id);
        if (existingNode) {
          // Keep the position from React Flow if node already exists
          return { ...newNode, position: existingNode.position };
        }
        return newNode;
      });
    });
  }, [dialogTree]);

  React.useEffect(() => {
    setEdges(getReactFlowEdges());
  }, [dialogTree, setEdges]);

  const onConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target) {
        const sourceNode = dialogTree.nodes[connection.source];

        if (sourceNode.type === 'dialog' && connection.sourceHandle) {
          const choiceIdx = parseInt(connection.sourceHandle.split('-')[1]);
          const updatedNodes = { ...dialogTree.nodes };
          const dialogNode = updatedNodes[connection.source] as DialogNodeData;

          if (dialogNode.choices && dialogNode.choices[choiceIdx]) {
            dialogNode.choices[choiceIdx].go = connection.target;
            setDialogTree({ ...dialogTree, nodes: updatedNodes });
          }
        }

        if (sourceNode.type === 'condition') {
          const updatedNodes = { ...dialogTree.nodes };
          const condNode = updatedNodes[connection.source] as ConditionNodeData;

          if (connection.sourceHandle === 'true') {
            condNode.on_true = connection.target;
          } else if (connection.sourceHandle === 'false') {
            condNode.on_false = connection.target;
          }

          setDialogTree({ ...dialogTree, nodes: updatedNodes });
        }
      }

      setEdges((eds) => addEdge(connection, eds));
    },
    [dialogTree, setEdges]
  );

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      setSelectedNodeId(node.id);
    },
    []
  );

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      // Save node position when dragging stops
      setDialogTree((prev) => ({
        ...prev,
        nodes: {
          ...prev.nodes,
          [node.id]: {
            ...prev.nodes[node.id],
            position: node.position,
          },
        },
      }));
    },
    []
  );

  // Helper function to calculate new node position
  const calculateNewNodePosition = () => {
    let newPosition = { x: 250, y: 100 };
    if (selectedNodeId && dialogTree.nodes[selectedNodeId]) {
      const selectedNode = dialogTree.nodes[selectedNodeId];
      if (selectedNode.position) {
        newPosition = {
          x: selectedNode.position.x + 400,
          y: selectedNode.position.y + Math.random() * 50 - 25,
        };
      }
    } else {
      const nodeCount = Object.keys(dialogTree.nodes).length;
      newPosition = {
        x: 250 + Math.random() * 100,
        y: 100 + nodeCount * 50,
      };
    }
    return newPosition;
  };

  const addDialogNode = () => {
    const newId = uid('dialog');
    const newPosition = calculateNewNodePosition();

    setDialogTree({
      ...dialogTree,
      nodes: {
        ...dialogTree.nodes,
        [newId]: { id: newId, type: 'dialog', npc: 'NPC: ', choices: [], position: newPosition },
      },
    });
    setSelectedNodeId(newId);
    setShowAddMenu(false);
  };

  const addActionNode = (actionId: string) => {
    const template = ACTION_TEMPLATES.find(t => t.id === actionId);
    const newId = uid('action');
    const newPosition = calculateNewNodePosition();

    setDialogTree({
      ...dialogTree,
      nodes: {
        ...dialogTree.nodes,
        [newId]: { id: newId, type: 'action', action: actionId, params: template?.params || {}, position: newPosition },
      },
    });
    setSelectedNodeId(newId);
    setShowAddMenu(false);
  };

  const addEventNode = (eventId: string) => {
    const template = EVENT_TEMPLATES.find(t => t.id === eventId);
    const newId = uid('event');
    const newPosition = calculateNewNodePosition();

    setDialogTree({
      ...dialogTree,
      nodes: {
        ...dialogTree.nodes,
        [newId]: { id: newId, type: 'event', event: eventId, params: template?.params || {}, position: newPosition },
      },
    });
    setSelectedNodeId(newId);
    setShowAddMenu(false);
  };

  const addConditionNode = (condType: string) => {
    const template = CONDITION_TYPES.find(t => t.id === condType);
    const newId = uid('cond');
    const newPosition = calculateNewNodePosition();

    setDialogTree({
      ...dialogTree,
      nodes: {
        ...dialogTree.nodes,
        [newId]: {
          id: newId,
          type: 'condition',
          condition_type: condType as any,
          params: template?.params || {},
          on_true: '',
          on_false: '',
          position: newPosition,
        },
      },
    });
    setSelectedNodeId(newId);
    setShowAddMenu(false);
  };

  const deleteNode = (nodeId: string) => {
    const updatedNodes = { ...dialogTree.nodes };
    delete updatedNodes[nodeId];

    Object.values(updatedNodes).forEach((node) => {
      if (node.type === 'dialog' && node.choices) {
        node.choices.forEach((choice) => {
          if (choice.go === nodeId) choice.go = '';
        });
      }
      if (node.type === 'condition') {
        if (node.on_true === nodeId) node.on_true = '';
        if (node.on_false === nodeId) node.on_false = '';
      }
    });

    setDialogTree({ ...dialogTree, nodes: updatedNodes });
    setSelectedNodeId(Object.keys(updatedNodes)[0] || '');
  };

  const updateNode = (nodeId: string, updates: Partial<AnyNodeData>) => {
    // Preserve current position from React Flow nodes if not in updates
    const currentReactFlowNode = nodes.find(n => n.id === nodeId);
    const currentPosition = currentReactFlowNode?.position || dialogTree.nodes[nodeId]?.position;

    setDialogTree({
      ...dialogTree,
      nodes: {
        ...dialogTree.nodes,
        [nodeId]: {
          ...dialogTree.nodes[nodeId],
          ...updates,
          // Preserve position unless explicitly updated
          position: updates.position || currentPosition,
        } as AnyNodeData,
      },
    });
  };

  const exportDialog = () => {
    const blob = new Blob([JSON.stringify(dialogTree, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${dialogTree.dialog_tree_id}.dialog.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedNode = selectedNodeId ? dialogTree.nodes[selectedNodeId] : null;
  const timeSinceLastSave = Math.floor((Date.now() - lastSaved) / 1000);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-slate-50">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50 shadow-sm">
        <div className="max-w-[1800px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Редактор диалогов</h1>
                <p className="text-sm text-slate-500">
                  ID: {dialogTree.dialog_tree_id} • Сохранено {timeSinceLastSave}с назад
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowProjectList(true)}>
                <FolderOpen className="w-4 h-4 mr-2" />
                Проекты
              </Button>
              <Button variant="outline" size="sm" onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                Сохранить
              </Button>
              <Button onClick={exportDialog} size="sm">
                <Download className="w-4 h-4 mr-2" />
                Экспорт JSON
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Project List Modal */}
      {showProjectList && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader>
              <CardTitle>Сохраненные проекты</CardTitle>
              <CardDescription>Выберите проект для загрузки</CardDescription>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1">
              {savedProjects.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <List className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>Нет сохраненных проектов</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {savedProjects.map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold">{project.name}</h3>
                        <p className="text-sm text-slate-500">
                          Обновлено: {new Date(project.updated).toLocaleString('ru')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleLoad(project)}>
                          Загрузить
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(project.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <div className="p-4 border-t">
              <Button variant="outline" className="w-full" onClick={() => setShowProjectList(false)}>
                Закрыть
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Main Content - продолжение в следующем сообщении из-за ограничения длины */}
      <div className="max-w-[1800px] mx-auto px-4 py-6">
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Граф диалога</CardTitle>
                <CardDescription>💬 Диалоги • ⚡ События • ⚙️ Действия • 🔀 Условия</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full h-[700px] border-2 border-slate-200 rounded-lg bg-white relative">
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onNodeClick={onNodeClick}
                    onNodeDragStop={onNodeDragStop}
                    nodeTypes={nodeTypes}
                    connectionMode={ConnectionMode.Loose}
                    fitView
                    snapToGrid
                    snapGrid={[15, 15]}
                  >
                    <Controls />
                    <Background variant={BackgroundVariant.Dots} gap={15} size={1} color="#c084fc" />

                    <Panel position="top-right" className="space-y-2">
                      <div className="relative">
                        <Button onClick={() => setShowAddMenu(!showAddMenu)} size="sm" className="w-full">
                          <Plus className="w-4 h-4 mr-2" />
                          Добавить узел
                          <ChevronDown className="w-4 h-4 ml-2" />
                        </Button>

                        {showAddMenu && (
                          <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border-2 p-2 w-64 z-50 max-h-[500px] overflow-y-auto">
                            <div className="space-y-2">
                              <Button variant="outline" size="sm" className="w-full justify-start" onClick={addDialogNode}>
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Диалог
                              </Button>

                              <Separator />

                              <div className="text-xs font-semibold text-slate-500 px-2">Условия:</div>
                              {CONDITION_TYPES.map((type) => (
                                <Button
                                  key={type.id}
                                  variant="outline"
                                  size="sm"
                                  className="w-full justify-start text-xs"
                                  onClick={() => addConditionNode(type.id)}
                                >
                                  <GitMerge className="w-3 h-3 mr-2" />
                                  {type.label}
                                </Button>
                              ))}

                              <Separator />

                              <div className="text-xs font-semibold text-slate-500 px-2">Действия:</div>
                              <div className="space-y-1">
                                {ACTION_TEMPLATES.map((template) => {
                                  const Icon = template.icon;
                                  return (
                                    <Button
                                      key={template.id}
                                      variant="outline"
                                      size="sm"
                                      className="w-full justify-start text-xs"
                                      onClick={() => addActionNode(template.id)}
                                    >
                                      <Icon className="w-3 h-3 mr-2" />
                                      {template.label}
                                    </Button>
                                  );
                                })}
                              </div>

                              <Separator />

                              <div className="text-xs font-semibold text-slate-500 px-2">События:</div>
                              {EVENT_TEMPLATES.map((template) => {
                                const Icon = template.icon;
                                return (
                                  <Button
                                    key={template.id}
                                    variant="outline"
                                    size="sm"
                                    className="w-full justify-start text-xs"
                                    onClick={() => addEventNode(template.id)}
                                  >
                                    <Icon className="w-3 h-3 mr-2" />
                                    {template.label}
                                  </Button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </Panel>
                  </ReactFlow>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Editor Panel */}
          <div className="col-span-1 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Настройки</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Dialog Tree ID</label>
                  <Input
                    value={dialogTree.dialog_tree_id}
                    onChange={(e) => setDialogTree({ ...dialogTree, dialog_tree_id: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Root Node</label>
                  <select
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={dialogTree.root}
                    onChange={(e) => setDialogTree({ ...dialogTree, root: e.target.value })}
                  >
                    {Object.keys(dialogTree.nodes).map((nodeId) => (
                      <option key={nodeId} value={nodeId}>{nodeId}</option>
                    ))}
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Node Editor - для каждого типа свой редактор */}
            {selectedNode && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">
                        {selectedNode.type === 'dialog' && '💬 Диалог'}
                        {selectedNode.type === 'action' && '⚙️ Действие'}
                        {selectedNode.type === 'event' && '⚡ Событие'}
                        {selectedNode.type === 'condition' && '🔀 Условие'}
                      </CardTitle>
                      <CardDescription className="text-xs">{selectedNodeId}</CardDescription>
                    </div>
                    {selectedNodeId !== dialogTree.root && (
                      <Button variant="destructive" size="sm" onClick={() => deleteNode(selectedNodeId)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Dialog Editor */}
                  {selectedNode.type === 'dialog' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Реплика NPC</label>
                        <Textarea
                          value={selectedNode.npc ?? ''}
                          onChange={(e) => updateNode(selectedNodeId, { npc: e.target.value })}
                          placeholder="NPC: Текст..."
                          rows={4}
                          className="text-sm"
                        />
                      </div>
                      <Separator />
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium">Варианты</label>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const choices = [...(selectedNode.choices || [])];
                              choices.push({ text: '', go: '' });
                              updateNode(selectedNodeId, { choices });
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Добавить
                          </Button>
                        </div>
                        {selectedNode.choices?.map((choice, idx) => (
                          <div key={idx} className="border rounded-lg p-2 space-y-2">
                            <div className="flex gap-2">
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">{idx + 1}</span>
                              <Input
                                value={choice.text}
                                onChange={(e) => {
                                  const choices = [...selectedNode.choices!];
                                  choices[idx] = { ...choices[idx], text: e.target.value };
                                  updateNode(selectedNodeId, { choices });
                                }}
                                placeholder="Текст"
                                className="h-8 text-sm"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const choices = selectedNode.choices!.filter((_, i) => i !== idx);
                                  updateNode(selectedNodeId, { choices });
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Action Editor */}
                  {selectedNode.type === 'action' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Действие</label>
                        <select
                          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                          value={selectedNode.action}
                          onChange={(e) => {
                            const template = ACTION_TEMPLATES.find(t => t.id === e.target.value);
                            updateNode(selectedNodeId, {
                              action: e.target.value,
                              params: template?.params || {},
                            });
                          }}
                        >
                          {ACTION_TEMPLATES.map((t) => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Параметры (JSON)</label>
                        <Textarea
                          value={JSON.stringify(selectedNode.params, null, 2)}
                          onChange={(e) => {
                            try {
                              const params = JSON.parse(e.target.value || '{}');
                              updateNode(selectedNodeId, { params });
                            } catch {}
                          }}
                          rows={5}
                          className="text-xs font-mono"
                        />
                      </div>
                    </>
                  )}

                  {/* Event Editor */}
                  {selectedNode.type === 'event' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Событие</label>
                        <select
                          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                          value={selectedNode.event}
                          onChange={(e) => {
                            const template = EVENT_TEMPLATES.find(t => t.id === e.target.value);
                            updateNode(selectedNodeId, {
                              event: e.target.value,
                              params: template?.params || {},
                            });
                          }}
                        >
                          {EVENT_TEMPLATES.map((t) => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Параметры (JSON)</label>
                        <Textarea
                          value={JSON.stringify(selectedNode.params, null, 2)}
                          onChange={(e) => {
                            try {
                              const params = JSON.parse(e.target.value || '{}');
                              updateNode(selectedNodeId, { params });
                            } catch {}
                          }}
                          rows={5}
                          className="text-xs font-mono"
                        />
                      </div>
                    </>
                  )}

                  {/* Condition Editor */}
                  {selectedNode.type === 'condition' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Тип условия</label>
                        <select
                          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                          value={selectedNode.condition_type}
                          onChange={(e) => {
                            const template = CONDITION_TYPES.find(t => t.id === e.target.value);
                            updateNode(selectedNodeId, {
                              condition_type: e.target.value as any,
                              params: template?.params || {},
                            });
                          }}
                        >
                          {CONDITION_TYPES.map((t) => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Параметры (JSON)</label>
                        <Textarea
                          value={JSON.stringify(selectedNode.params, null, 2)}
                          onChange={(e) => {
                            try {
                              const params = JSON.parse(e.target.value || '{}');
                              updateNode(selectedNodeId, { params });
                            } catch {}
                          }}
                          rows={3}
                          className="text-xs font-mono"
                        />
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-xs">
                        <div className="font-semibold text-orange-900 mb-2">Подключения:</div>
                        <div className="space-y-1 text-orange-700">
                          <div>🟢 Правый выход → TRUE</div>
                          <div>🔴 Левый выход → FALSE</div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* JSON Preview */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Экспорт JSON</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(dialogTree, null, 2));
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Копировать
                </Button>
                <Button onClick={exportDialog} size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Скачать
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg overflow-auto max-h-[400px] text-xs font-mono">
              {JSON.stringify(dialogTree, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
